import {
  EnvTypes,
  EvalEnv,
  Plugins,
  Variable,
  withSystem,
} from '@packages/systems/wfdl';
import {AssetPluginType, ImagePluginType} from './AssetPluginType';
import {IMAGE_PLACEHOLDER_FULL_ID} from '@packages/systems/plugins-utils/Asset';
import {TTypeConstructor, TText, functionOf} from '@packages/systems/wfdl-ast';
import {identifier, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  toBooleanTag,
  TPredicate,
} from '@packages/systems/plugins-utils/Boolean';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {weakMemo} from '@packages/utilities/memo';

const ImageType = TTypeConstructor(
  qualify(Plugins.name('Asset'), typeName('Image'))
);

// Asset::Image binding values arrive as `{url}` for CMS-hydrated sources,
// but as `{id}` only for property and page-binding sources -- the CDN url
// for those lives in the asset store, not in the value. Curry the converter
// over an `AssetUrlResolver` so render-context callers (canvas, SSR) can
// bind a real resolver via addAssetUtilsToEnv. Default registration uses
// the no-op resolver and falls through to '' when only an id is present.
export type AssetUrlResolver = (id: string) => string | undefined;

const noopAssetUrlResolver: AssetUrlResolver = () => undefined;

const _imageToText = {
  envType: {
    dataType: functionOf(withSystem({isBindable: true}, ImageType), TText),
    exported: 'hidden' as const,
    implicit: 'prefilledArgs' as const,
  },
  value:
    (resolveAssetUrl: AssetUrlResolver) =>
    (image: unknown): string => {
      if (!image || typeof image !== 'object') return '';
      const data = image as {url?: unknown; id?: unknown};
      if (typeof data.url === 'string' && data.url) return data.url;
      if (
        typeof data.id === 'string' &&
        data.id &&
        data.id !== IMAGE_PLACEHOLDER_FULL_ID
      ) {
        return resolveAssetUrl(data.id) ?? '';
      }
      return '';
    },
} as const;

const imageToText: EnvTypes.TypedValue = {
  envType: _imageToText.envType,
  value: _imageToText.value(noopAssetUrlResolver),
};

const IMAGE_TO_TEXT_VARIABLE = Variable.qualified(
  qualify(Plugins.name('Asset'), identifier('imageToText'))
);

// Re-binds Asset::imageToText with a concrete resolver so render-context
// callers can produce real CDN urls for property/page-binding image values.
// Nested weakMemo keeps env identity stable across renders without running
// object equality on this hot path.
const addAssetUtilsToEnvForEvalEnv = weakMemo((env: EvalEnv.EvalEnvType) =>
  weakMemo((resolveAssetUrl: AssetUrlResolver) =>
    EvalEnv.insertValue(
      env,
      IMAGE_TO_TEXT_VARIABLE,
      _imageToText.value(resolveAssetUrl)
    )
  )
);

export const addAssetUtilsToEnv = (
  env: EvalEnv.EvalEnvType,
  resolveAssetUrl: AssetUrlResolver
): EvalEnv.EvalEnvType => addAssetUtilsToEnvForEvalEnv(env)(resolveAssetUrl);

// Convenience factory that produces a stable AssetUrlResolver from a render
// asset map. Stable identity matters because addAssetUtilsToEnv caches on
// (env, resolver) -- a fresh closure per render would invalidate the cache
// and rebuild the env on every canvas tick. weakMemo keys on the asset-map
// reference, which Flux already keeps stable until the store updates.
export const createAssetUrlResolver = weakMemo(
  (assets: {[id: string]: {cdnUrl?: string} | undefined}): AssetUrlResolver =>
    (id: string) =>
      assets[id]?.cdnUrl
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    Asset: AssetPluginType,
    Image: ImagePluginType,
  },
  values: {
    imageCondition: operand(ImageType),
    imageIsSet: {
      envType: {
        dataType: withSystem({label: 'is set'}, TPredicate(ImageType)),
        exported: 'visible',
      },
      value: (image: unknown) => {
        return toBooleanTag(isImageSet(image));
      },
    },
    imageIsNotSet: {
      envType: {
        dataType: withSystem({label: 'is not set'}, TPredicate(ImageType)),
        exported: 'visible',
      },
      value: (image: unknown) => {
        return toBooleanTag(!isImageSet(image));
      },
    },
    imageToText,
  },
});

const isImageSet = (image: unknown): boolean => {
  if (!image) {
    return false;
  }
  if (typeof image === 'object') {
    if ('id' in image && image.id) {
      return image.id !== IMAGE_PLACEHOLDER_FULL_ID;
    }
    if ('url' in image && image.url) {
      return true;
    }
  }
  return false;
};
